<template>
  <v-layout class="catalog grey lighten-4" column>
    <div class="noto bold pa-3 d-md-flex align-center" width="100%">
      <div class="tittle primary--text">{{ section }}</div>
      <v-spacer/>
      <v-tooltip bottom>
        <template
          v-slot:activator="{ on }">
          <v-btn
            :href="exportRoute"
            class="mr-3"
            v-on="on"
            target="_blank"
            dark
            fab
            x-small
            depressed
            color="secondary">
            <v-icon color="white">mdi-application-export</v-icon>
          </v-btn>
        </template>
        <span>Exportar órdenes confirmadas a CSV</span>
      </v-tooltip>
      <div class="d-flex justify-end align-center mr-3">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Busca ordenes por id"
          solo
          hide-details
          rounded
          flat
          dense/>
      </div>
    </div>

    <v-flex xs12>
      <v-alert dense type="info">
        <span>Recuerda cargar la factura antes de entregar la mercancía para asegurar el pago en Eato.</span>
      </v-alert>
      <v-data-table
        :loading-text="table.title"
        :server-items-length="table.total"
        :loading="table.loading"
        :headers="headers"
        :items="table.data"
        :options="table.options"
        :footer-props="{
          'items-per-page-options': [20, 50, 100, 200]
        }"
        sort-by="updatedAt"
        @update:options="updateTable"
        class="elevation-1 fixed-header">
        <template v-slot:top>
          <div class="px-3 filters">
            <v-row>
              <v-col md="3" cols="12">
                <div class="d-flex align-center">
                  <span class="mr-3">
                    Filtros:
                  </span>
                  <v-menu
                    ref="datePicker"
                    v-model="datePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="range"
                        label="Rango de fechas"
                        prepend-icon="mdi-calendar"
                        readonly
                        hide-details
                        dense
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="dates"
                      :range="true"
                      first-day-of-week="1"
                      no-title
                      :max="untilNow"
                      locale="es-mx"
                    >
                      <div class="pb-2" style="overflow-x: auto;">
                        <div class="d-flex">
                          <v-btn text color="primary" @click="resetDate">Borrar</v-btn>
                          <v-spacer/>
                          <v-btn text color="grey" @click="datePicker = false">Cancel</v-btn>
                          <v-btn :disabled="dates.length < 2" color="primary" @click="filterData">OK</v-btn>
                        </div>
                      </div>
                    </v-date-picker>
                  </v-menu>
                </div>
              </v-col>
              <v-col md="3" cols="12">
                <v-select
                  v-model="restaurant"
                  :items="restaurants"
                  item-text="name"
                  item-value="_id"
                  label="Restaurante"
                  hide-details
                  dense
                  @change="filterData"
                />
              </v-col>
            </v-row>
          </div>
        </template>
        <template v-slot:item.uuid="{ item }">
          <a href="" @click.prevent="openDialog(item)">
            {{ item.uuid }}
          </a>
        </template>
        <template v-slot:item.status="{ item }">
          <v-tooltip
            top
            max-width="300">
            <template v-slot:activator="{ on }">
              <v-btn x-small icon v-on="on">
                <v-icon color="dark-grey">mdi-information</v-icon>
              </v-btn>
            </template>
            <span>
              {{ statusDescriptions(item.status) }}
            </span>
          </v-tooltip>
          <span :class="`${statusColors(item.status)}--text`">
            {{ statusNames(item.status) }}
          </span>
        </template>
        <template v-slot:item.total="{ item }">
          <span>
            {{ item.total | toFixNumber(2) | numberWithComma | prefix('$') }}
          </span>
        </template>
        <template v-slot:item.restaurant="{ item }">
           <v-tooltip
            top
            max-width="300">
            <template v-slot:activator="{ on }">
              <v-btn x-small icon v-on="on">
                <v-icon color="dark-grey">mdi-information</v-icon>
              </v-btn>
            </template>
            <span>
              RFC: {{ item.taxId }}
            </span>
            <br>
            <span>
              Nombre Fiscal: {{ item.taxName }}
            </span>
          </v-tooltip>
          <span>
            {{ item.restaurant }}
          </span>
        </template>
        <template v-slot:item.createdAt="{ item }">
          {{ item.createdAt | toDate }}
          <v-tooltip class="style-tool" left>
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
                class="font-weight-bold"
              >
                <v-icon small>mdi-information</v-icon>
              </span>
            </template>
            <v-timeline>
              <v-timeline-item
                small
                v-for="(item, index) in item.statusChanges"
                :key="`item-${index}`"
              >
                <strong>{{ statusNames(item.name) }}</strong>
                <div style="font-size: 10px;">
                  {{ item.createdAt | toDateTime }}
                </div>
              </v-timeline-item>
            </v-timeline>
          </v-tooltip>
        </template>
        <template v-slot:item.actions="{ item }">
          <v-btn
            v-on:click="openDialog(item)"
            block
            depressed
            color="success"
            class="my-3"
            v-if="$isMobile && ['provider'].includes($is())">
            Ver orden de compra
          </v-btn>
        </template>
        <template v-slot:item.waitTime="{ item }">
          {{ item.waitTime }}
        </template>
      </v-data-table>
    </v-flex>

    <detail-dialog
      :dialog="dialog"
      :order.sync="order"
      v-on:update:order="order = $event"
      @close="closeDialog"
      @update="updateOrders"
      @remove ="removeOrder"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

  </v-layout>
</template>

<script>
import moment from 'moment'
import DetailDialog from '@/screens/providers/DetailPurchaseOrder'
import Order from '@/models/Order'
import OrdersList from '@/models/collections/OrdersList'
import { debounce } from 'lodash'

export default {
  name: 'Orders',
  components: { DetailDialog },
  data () {
    let ordersList = new OrdersList()
    let orderModel = new Order({}, ordersList)

    return {
      orderModel,
      ordersList,
      datePicker: false,
      dates: [],
      section: 'Pedidos por confirmar',
      overlay: false,
      dialog: false,
      search: '',
      status: [],
      restaurant: '',
      order: {},
      table: {
        title: 'Loading orders',
        loading: true,
        data: [],
        total: 0,
        headers: [],
        options: {
          itemsPerPage: 100,
          page: 1
        }
      }
    }
  },
  watch: {
    'search': debounce(async function (val) {
      try {
        const params = {
          skip: 0,
          uuid: `/${val}/gi`,
          status: 'sent',
          restaurant: this.restaurant
        }
        if (this.dates.length) {
          this.dates.sort((a, b) => {
            if (a < b) return -1
            if (a > b) return 1
            return 0
          })
          params['createdAt>'] = this.$moment(this.dates[0]).startOf('day').toISOString() || ''
          params['createdAt<'] = this.$moment(this.dates[1]).endOf('day').toISOString() || ''
        }
        await this.load(params)
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
    }, 300)
  },
  computed: {
    exportRoute () {
      const dates = this.dates.length === 2
        ? [this.$moment(this.dates[0]).startOf('day').toISOString(), this.$moment(this.dates[1]).endOf('day').toISOString()]
        : [null, null]
      let url = `${process.env.VUE_APP_API_URL}/v1/orders/export/csv`
      url += `?access_token=${this.$store.state.login.token}`
      url += `&provider=${this.$store.state.currentProvider.data._id}`
      url += `&isUserProvider=true`
      url += `&status=sent`
      url += `&isRead=true`
      if (this.restaurant) url += `&restaurant=${this.restaurant}`
      if (this.dates.length) url += `&createdAt>=${dates[0]}&createdAt<=${dates[1]}`
      return url
    },
    untilNow () {
      return moment().format('YYYY-MM-DD')
    },
    ieps () {
      return this.table.data.reduce((acc, num) => {
        const ieps = num.products.reduce((prodAcc, prod) => prodAcc + this.calculateIepsProduct(prod), 0)
        return acc + ieps
      }, 0)
    },
    iva () {
      return this.table.data.reduce((acc, num) => acc + (num.IVA || 0), 0)
    },
    subtotal () {
      return this.table.data.reduce((acc, num) => acc + (num.subtotalNotTax || 0), 0)
    },
    subtotalTax () {
      return this.table.data.reduce((acc, num) => acc + (num.subtotalTax || 0), 0)
    },
    total () {
      return this.table.data.reduce((acc, num) => acc + (num.totalInvoiced || 0), 0)
    },
    statuses () {
      return [
        { text: 'Nueva orden', value: 'sent' },
        { text: 'Confirmada', value: 'confirmed' },
        { text: 'Cancelada', value: 'canceled' },
        { text: 'Facturada', value: 'invoiced' },
        { text: 'Entregada', value: 'received' },
        { text: 'Pendiente de pago', value: 'pending_payment' },
        { text: 'Cobrada', value: 'paid' }
      ]
    },
    range () {
      return this.dates.length < 2 ? '' : `${this.dates[0]} / ${this.dates[1]}`
    },
    headers () {
      const headers = [
        { text: 'No. de Orden de compra', value: 'uuid' },
        { text: 'Restaurant', value: 'restaurant' },
        { text: 'Fecha de emisión', value: 'createdAt' },
        { text: 'Nombre del emisor', value: 'author.email', sortable: false },
        { text: 'Monto', value: 'total', sortable: false },
        { text: 'Estatus', value: 'status', sortable: false },
        // { text: 'Última modificación', value: 'ago', sortable: false },
        { text: 'Última modificación', value: 'waitTime', sortable: false }
      ]
      if (this.$isMobile) {
        headers.push({ text: '', value: 'actions' })
      }
      return headers
    },
    restaurants () {
      const restaurants = [...this.$store.state.currentProvider.restaurants.data]
      restaurants.unshift({
        name: 'Todos',
        _id: ''
      })
      return restaurants
    }
  },
  methods: {
    calculateIepsProduct (product) {
      const ieps = product.taxes.find(tax => tax.satCode === '003')
      return ieps
        ? ieps.base
        : 0
    },
    calculateIvaProduct (product) {
      const iva = product.taxes.find(tax => tax.satCode === '002')
      return iva
        ? product.total * (iva.base / 100)
        : 0
    },
    updateOrders (order) {
      const index = this.ordersList.models.findIndex(mod => mod.id === order.id)
      this.ordersList.models[index] = order
      this.ordersList.replace(this.ordersList.models)
      this.table.data = this.parsedData()
      this.$store.dispatch('getOrdersQuantity')
    },
    removeOrder (order) {
      this.ordersList.remove({ id: order.id })
      this.table.data = this.parsedData()
    },
    async resetDate () {
      this.dates = []
      await this.load()
      this.datePicker = false
    },
    async filterData () {
      const params = {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: '-updatedAt',
        status: 'sent'
      }
      if (this.restaurant) {
        params.restaurant = this.restaurant
      }

      if (this.dates.length) {
        this.dates.sort((a, b) => {
          if (a < b) return -1
          if (a > b) return 1
          return 0
        })
        params['createdAt>'] = this.$moment(this.dates[0]).startOf('day').toISOString() || ''
        params['createdAt<'] = this.$moment(this.dates[1]).endOf('day').toISOString() || ''
      }

      await this.load(params)
      this.datePicker = false
    },
    statusNames (status) {
      return {
        sent: 'Nueva orden',
        confirmed: 'Confirmada',
        canceled: 'Cancelada',
        invoiced: 'Facturada',
        invalid: 'Factura no coincide',
        received: 'Entregada',
        pending_payment: 'Pendiente de pago',
        paid: 'Cobrada'
      }[status]
    },
    statusColors (status) {
      return {
        created: 'grey',
        sent: 'grey',
        confirmed: 'info',
        canceled: 'red',
        invoiced: 'info',
        invalid: 'red',
        received: 'primary',
        pending_payment: 'primary',
        paid: 'primary'
      }[status]
    },
    statusDescriptions (status) {
      return {
        created: 'Orden de compra que no se ha enviado al proveedor',
        sent: 'Orden de compra enviada al proveedor, el proveedor debe confirmar el producto solicitado.',
        confirmed: 'El proveedor confirmó la orden y el producto solicitado.',
        canceled: 'Orden de compra cancelada.',
        invoiced: 'El proveedor o el restaurante ya subió la factura de esta orden de compra. La orden se encuentra en espera de recibir la mercancía.',
        invalid: 'El contenido de la factura adjunta a la orden de compra no coincide con la información en la orden de compra.',
        received: 'El restaurante recibió y facturó correctamente la mercancía.',
        pending_payment: 'La orden de compra está a crédito.',
        paid: 'La orden de compra fue pagada correctamente.'
      }[status]
    },
    closeDialog: function (order) {
      this.dialog = false
      this.overlay = false
      this.order = new Order()
    },
    openDialog (item) {
      this.order = item
      this.dialog = true
    },
    updateTable: async function (data) {
      const { sortBy, sortDesc } = data
      if (sortBy[0] === 'updatedAt') {
        sortDesc[0] = true
      }
      const params = {
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        sort: `${sortDesc[0] ? '-' : ''}${sortBy[0]}`,
        status: 'sent'
      }
      if (this.restaurant) {
        params.restaurant = this.restaurant
      }
      if (this.dates.length) {
        this.dates.sort((a, b) => {
          if (a < b) return -1
          if (a > b) return 1
          return 0
        })
        params['createdAt>'] = this.dates[0] || ''
        params['createdAt<'] = this.dates[1] || ''
      }
      await this.load(params)
    },
    async load (params = null) {
      this.table.loading = true
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        sort: '-updatedAt',
        status: 'sent'
      }
      params.isRead = true
      if (this.restaurant) {
        params.restaurant = this.restaurant
      }
      try {
        const { response } = await this.orderModel.fetch(params)
        this.ordersList.replace([response.data.data])
        this.table.data = this.parsedData()
        this.table.total = response.data.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false

      if (this.$route.params.id) {
        const item = this.table.data.find(order => order.id === this.$route.params.id)
        this.openDialog(item)
      }
    },
    parsedData () {
      return this.ordersList.models
        .filter(order => order.status === 'sent' && order.isRead)
        .map(order => {
          order.statusChanges.map((item, i) => {
            if (i + 1 === order.statusChanges.length) {
              order.waitTime = moment(item.createdAt).fromNow()
            }
          })

          return Object.assign(
            order,
            {
              taxId: order.$.restaurant.taxInformation.taxId,
              taxName: order.$.restaurant.taxInformation.taxName,
              restaurant: order.$.restaurant.name,
              ago: moment(order.updatedAt).fromNow()
            }
          )
        })
    }
  }
}
</script>
